import React from 'react';
import styled from 'styled-components';
import { CheckCircle, Download } from '@mui/icons-material';
import FlexboxContainer from 'components/UI/FlexboxContainer';
import Button from 'components/UI/Button';
import WarningBanner from 'components/UI/WarningBanner';
import { useAPI } from 'lib/API';
import { downloadDocument } from 'requests/documents';
import { renderToast, toast } from 'components/UI/ToastNotification';
import { formatDateTime } from 'utils/formatters';
import { Main, Text, Border } from 'styles/themeColors';
import { AFFILIATE_QUESTIONNAIRE_CATEGORY } from '../../constants/affiliate';

const Box = styled.div`
  border: 1px solid ${Border.light};
  border-radius: 6px;
  margin: 4px 0px 10px 0px;
`;

const Heading = styled.div`
  font-size: 16px;
  font-weight: bold;
  color: ${Text.primary};
`;

const CheckLabel = styled.span`
  color: ${Main.success};
  font-size: 14px;
  font-weight: bold;
  margin-left: 6px;
`;

const FieldLabel = styled.span`
  font-size: 14px;
  color: ${Text.secondary};
  margin-right: 4px;
`;

const FieldValue = styled.span`
  font-size: 14px;
  color: ${Text.primary};
  word-break: break-all;
`;

/** Locate the affiliate questionnaire PDF within consignment documents. */
const findAffiliateDoc = (documents?: any) =>
  (documents?.documentGroups || [])
    .flatMap((dg: any) => dg?.documents || [])
    .find((d: any) => d?.category?.name === AFFILIATE_QUESTIONNAIRE_CATEGORY);

/** Trigger a browser download for a blob. */
const triggerBlobDownload = (blob: Blob, filename: string) => {
  const a = document.createElement('a');
  a.style.display = 'none';
  document.body.appendChild(a);
  const url = window.URL.createObjectURL(blob);
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
};

/**
 * Success state: the affiliate form was submitted and its PDF is attached.
 * Owns the download flow so the hook is only instantiated on this path.
 */
const SubmittedBox = ({ documents, submittedOn }: { documents?: any; submittedOn?: string }) => {
  const affiliateDoc = findAffiliateDoc(documents);

  const downloadDocumentReq = useAPI({
    deferFn: downloadDocument,
    onResolve: result => {
      if (result?.document) {
        triggerBlobDownload(result.document, affiliateDoc?.name || 'affiliate-questionnaire.pdf');
      }
    },
    onReject: (err: any) => {
      renderToast(toast.TYPE.ERROR, err?.message || 'Failed to download document');
    }
  });

  const onDownload = () => {
    if (affiliateDoc) {
      downloadDocumentReq.run(affiliateDoc.id, affiliateDoc.url);
    }
  };

  return (
    <Box>
      <FlexboxContainer flexDirection='column' gap='12px' >
        <FlexboxContainer width='100%' justifyContent='space-between' flexWrap='wrap' gap='8px' flexDirection='column' padding='12px 16px'>
          <Heading>Affiliate form submitted</Heading>
          <FlexboxContainer alignItems='center'>
            <CheckCircle style={{ color: Main.success, fontSize: 20 }} />
            <CheckLabel>Affiliate form attached to this order</CheckLabel>
          </FlexboxContainer>
        </FlexboxContainer>

        <FlexboxContainer width='100%' justifyContent='space-between' alignItems='center' flexWrap='wrap' gap='16px' borderTop={`1px solid ${Border.light}`} padding='12px 16px'>
          <FlexboxContainer gap='32px'> 
            <FlexboxContainer alignItems='center' gap='28px' flexWrap='wrap'>
              <FlexboxContainer flexDirection='column'>
                <FieldLabel>Form title:</FieldLabel>
                <FieldValue>Affiliate Questionnaire</FieldValue>
              </FlexboxContainer>
            </FlexboxContainer>
            <FlexboxContainer flexDirection='column'>
              <FieldLabel>Submitted On:</FieldLabel>
              <FieldValue>
                {submittedOn ? formatDateTime(submittedOn, true) : '—'}
              </FieldValue>
            </FlexboxContainer>
          </FlexboxContainer>
          {affiliateDoc && (
            <FlexboxContainer gap='16px' alignItems='center'>
              <FlexboxContainer flexDirection='column'>
                <FieldLabel>Attached file:</FieldLabel>
                <FieldValue>{affiliateDoc?.name}</FieldValue>
              </FlexboxContainer>
              <FlexboxContainer gap='8px'>
                <Button
                  onClick={onDownload}
                  disabled={downloadDocumentReq.isLoading}
                  leftIcon={() => <Download style={{ color: Main.white, fontSize: 18 }} />}
                >
                  Download PDF
                </Button>
              </FlexboxContainer>
            </FlexboxContainer>
          )}
        </FlexboxContainer>
      </FlexboxContainer>
    </Box>
  );
};

interface Props {
  documents?: any;
  /** Tri-state flag from the consignment: true = done, false = failed, null = pending. */
  isAffiliateFormPdfGenerated?: boolean | null;
  /** The date and time when the affiliate form was submitted. */
  submittedOn?: string;
  /** Whether the component is visible. */
  isVisible?: boolean;
}

/**
 * Surfaces the state of the Cognito affiliate questionnaire for an
 * affiliate-program consignment.
 *
 * - flag `true`  -> success box with the attached PDF + download
 * - flag `null`  -> "Preparing Affiliate form..." warning
 * - flag `false` -> "The submitted Affiliate form could not be prepared." error
 */
const AffiliateFormSubmitted = ({ documents, isAffiliateFormPdfGenerated, submittedOn, isVisible }: Props) => {
  if (!isVisible) {
    return null;
  }

  if (isAffiliateFormPdfGenerated === false) {
    return (
      <WarningBanner
        message='The submitted Affiliate form could not be prepared.'
        fontSize='14px'
        margin='4px 0 10px 0'
        type='error'
      />
    );
  }

  if (isAffiliateFormPdfGenerated !== true) {
    return (
      <WarningBanner
        message='Preparing Affiliate form...'
        fontSize='14px'
        margin='4px 0 10px 0'
      />
    );
  }

  return <SubmittedBox documents={documents} submittedOn={submittedOn} />;
};

export default AffiliateFormSubmitted;
